import React, { useState } from "react";
import { ArrowDown, ArrowUp, Filter, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Column header with integrated sort + filter (popover).
 *
 * @param {object} props
 * @param {import('../lib/dataTable').DataTableColumn} props.column
 * @param {ReturnType<import('../hooks/useDataTable').useDataTable>} props.table
 * @param {string} [props.className]
 * @param {string} [props.testId]
 */
export default function DataTableColumnHeader({ column, table, className, testId }) {
  const [open, setOpen] = useState(false);
  const sortable = column.sortable !== false;
  const filterable = column.filterable !== false && column.filterType;
  const hasMenu = sortable || filterable;

  const sorted = table.isColumnSorted(column.id);
  const sortDesc = table.sorting?.desc;
  const filtered = table.isColumnFiltered(column.id);
  const filterValue = table.getColumnFilter(column.id);

  if (!hasMenu) {
    return (
      <th className={cn("px-3 py-2 font-medium text-[10px] uppercase tracking-widest", className)}>
        {column.label}
      </th>
    );
  }

  return (
    <th className={cn("px-3 py-2 font-medium", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            data-testid={testId || `col-header-${column.id}`}
            className={cn(
              "inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest hover:text-foreground transition-colors",
              (sorted || filtered) && "text-foreground font-semibold",
            )}
          >
            {column.label}
            <span className="inline-flex items-center gap-0.5">
              {filtered && <Filter className="h-3 w-3 text-primary" aria-hidden />}
              {sorted && (sortDesc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
              {!sorted && !filtered && <Filter className="h-3 w-3 opacity-30" aria-hidden />}
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-56 p-3 space-y-3" align="start">
          {sortable && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Sort</p>
              <div className="flex flex-col gap-1">
                <Button
                  type="button"
                  variant={sorted && !sortDesc ? "secondary" : "ghost"}
                  size="sm"
                  className="justify-start h-8 text-xs"
                  onClick={() => table.setColumnSort(column.id, false)}
                >
                  <ArrowUp className="h-3.5 w-3.5 mr-2" />
                  {column.filterType === "date" ? "Oldest first" : "Ascending"}
                </Button>
                <Button
                  type="button"
                  variant={sorted && sortDesc ? "secondary" : "ghost"}
                  size="sm"
                  className="justify-start h-8 text-xs"
                  onClick={() => table.setColumnSort(column.id, true)}
                >
                  <ArrowDown className="h-3.5 w-3.5 mr-2" />
                  {column.filterType === "date" ? "Newest first" : "Descending"}
                </Button>
              </div>
            </div>
          )}

          {filterable && (
            <div className="space-y-1.5">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Filter</p>
              {column.filterType === "select" ? (
                <div className="flex flex-col gap-1 max-h-48 overflow-y-auto">
                  {(column.filterOptions || []).map((opt) => (
                    <Button
                      key={opt.value || "__all__"}
                      type="button"
                      variant={filterValue === opt.value ? "secondary" : "ghost"}
                      size="sm"
                      className="justify-start h-8 text-xs font-normal"
                      onClick={() => table.setColumnFilter(column.id, opt.value)}
                    >
                      {opt.label}
                    </Button>
                  ))}
                </div>
              ) : (
                <Input
                  type={column.filterType === "date" ? "date" : "text"}
                  value={filterValue}
                  onChange={(e) => table.setColumnFilter(column.id, e.target.value)}
                  placeholder={
                    column.filterType === "date"
                      ? undefined
                      : `Search ${column.label.toLowerCase()}…`
                  }
                  className="h-8 text-xs"
                  data-testid={`col-filter-${column.id}`}
                />
              )}
            </div>
          )}

          {(filtered || sorted) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full h-8 text-xs text-muted-foreground"
              onClick={() => {
                table.clearColumnFilter(column.id);
                if (sorted) table.clearColumnSort();
              }}
            >
              <X className="h-3.5 w-3.5 mr-1" />
              Reset column
            </Button>
          )}
        </PopoverContent>
      </Popover>
    </th>
  );
}

/**
 * Plain header cell (no sort/filter menu).
 */
export function DataTableHeaderCell({ children, className }) {
  return (
    <th className={cn("px-3 py-2 font-medium text-[10px] uppercase tracking-widest text-muted-foreground", className)}>
      {children}
    </th>
  );
}
